/**
 * Koko's intro audio bed. Mirrors `CompanionManager.playStartupAudio()`:
 *
 *   - `ff.mp3` background music at 0.12 volume (deliberately quiet,
 *     sits under the jingle), plays 3s, then fades to 0 over 0.6s.
 *   - `kookoo_intro.mp3` voice jingle at 0.85 volume, plays once.
 *
 * Browser autoplay note: Chrome/Safari will reject `audio.play()` until
 * the user interacts with the page. If we can't start, we silently
 * swallow the rejection — the intro bird still plays; just without sound.
 * Once the user clicks or presses a key, later TTS calls work fine.
 */

const BGM_VOLUME = 0.12;
const JINGLE_VOLUME = 0.85;
const BGM_PLAY_SECONDS = 3.0;
const BGM_FADE_SECONDS = 0.6;
const BGM_FADE_STEPS = 20;

export interface StartupAudioOptions {
  audioBaseUrl: string;
}

export class StartupAudio {
  private readonly options: StartupAudioOptions;
  private bgmAudio: HTMLAudioElement | null = null;
  private jingleAudio: HTMLAudioElement | null = null;
  private fadeTimerId: number | null = null;

  constructor(options: StartupAudioOptions) {
    this.options = options;
  }

  async playIntro(): Promise<void> {
    const base = this.options.audioBaseUrl.replace(/\/$/, "");

    this.bgmAudio = new Audio(`${base}/ff.mp3`);
    this.bgmAudio.volume = BGM_VOLUME;
    this.bgmAudio.loop = false;

    this.jingleAudio = new Audio(`${base}/kookoo_intro.mp3`);
    this.jingleAudio.volume = JINGLE_VOLUME;

    try {
      await Promise.allSettled([this.bgmAudio.play(), this.jingleAudio.play()]);
    } catch {
      // Autoplay blocked — silent intro is fine.
    }

    window.setTimeout(() => this.fadeOutBackgroundMusic(), BGM_PLAY_SECONDS * 1000);
  }

  private fadeOutBackgroundMusic(): void {
    if (!this.bgmAudio) return;
    const audio = this.bgmAudio;
    const stepMs = (BGM_FADE_SECONDS * 1000) / BGM_FADE_STEPS;
    const volumeDelta = audio.volume / BGM_FADE_STEPS;
    let stepsRemaining = BGM_FADE_STEPS;

    this.fadeTimerId = window.setInterval(() => {
      stepsRemaining -= 1;
      const nextVolume = Math.max(0, audio.volume - volumeDelta);
      audio.volume = nextVolume;
      if (stepsRemaining <= 0) {
        audio.pause();
        if (this.fadeTimerId !== null) {
          window.clearInterval(this.fadeTimerId);
          this.fadeTimerId = null;
        }
      }
    }, stepMs);
  }

  destroy(): void {
    if (this.fadeTimerId !== null) {
      window.clearInterval(this.fadeTimerId);
      this.fadeTimerId = null;
    }
    this.bgmAudio?.pause();
    this.jingleAudio?.pause();
    this.bgmAudio = null;
    this.jingleAudio = null;
  }
}
